import { useEffect, useCallback, useRef } from "react";
import * as URLConfig from "@frameworks/web/config/domain";
const publicUrl = URLConfig.config.rootUrl + URLConfig.config.web_config_access_reports;
export const useCacheInvalidate = (options) => {
    const { checkInterval = 0, maxRetries = 3, retryDelay = 1000, onVersionUpdate = null, enableServiceWorkerUpdate = true, aggressiveCacheBusting = true, } = options;
    const retryCountRef = useRef(0);
    const intervalRef = useRef(null);
    const safeLocalStorage = {
        getItem: (key) => {
            try {
                return localStorage.getItem(key);
            }
            catch (e) {
                console.warn(`Failed to read from localStorage: ${key}`, e);
                return null;
            }
        },
        setItem: (key, value) => {
            try {
                localStorage.setItem(key, value);
                return true;
            }
            catch (e) {
                console.warn(`Failed to write to localStorage: ${key}`, e);
                return false;
            }
        },
        removeItem: (key) => {
            try {
                localStorage.removeItem(key);
                return true;
            }
            catch (e) {
                console.warn(`Failed to remove from localStorage: ${key}`, e);
                return false;
            }
        },
    };
    // More aggressive cache clearing
    const clearAllCaches = async () => {
        const clearPromises = [];
        // 1. Clear Service Worker caches
        if (window === null || window === void 0 ? void 0 : window.caches) {
            try {
                const cacheNames = await window.caches.keys();
                const cacheDeletionPromises = cacheNames.map((name) => window.caches.delete(name));
                clearPromises.push(...cacheDeletionPromises);
                console.log("Service Worker caches marked for deletion");
            }
            catch (error) {
                console.error("Failed to clear Service Worker caches:", error);
            }
        }
        // 2. Clear Application Cache (deprecated but still present in some browsers)
        if (window === null || window === void 0 ? void 0 : window.applicationCache) {
            try {
                window === null || window === void 0 ? void 0 : window.applicationCache.update();
                console.log("Application cache updated");
            }
            catch (error) {
                console.error("Failed to update application cache:", error);
            }
        }
        // 3. Force reload of critical assets with cache busting
        if (aggressiveCacheBusting) {
            await bustAssetCache();
        }
        await Promise.all(clearPromises);
        console.log("All available caches cleared");
    };
    // Aggressive asset cache busting
    const bustAssetCache = async () => {
        const timestamp = Date.now();
        const bustPromises = [];
        // Get all link and script tags
        const links = document.querySelectorAll('link[rel="stylesheet"], link[rel="preload"]');
        const scripts = document.querySelectorAll("script[src]");
        // Bust CSS files
        links.forEach((link) => {
            if (link.href && !link.href.includes("?")) {
                const bustPromise = fetch(`${link.href}?bust=${timestamp}`, {
                    method: "HEAD",
                    cache: "no-cache",
                }).catch(() => { }); // Ignore errors for HEAD requests
                bustPromises.push(bustPromise);
            }
        });
        // Bust JS files
        scripts.forEach((script) => {
            if (script.src && !script.src.includes("?")) {
                const bustPromise = fetch(`${script.src}?bust=${timestamp}`, {
                    method: "HEAD",
                    cache: "no-cache",
                }).catch(() => { }); // Ignore errors for HEAD requests
                bustPromises.push(bustPromise);
            }
        });
        // Wait for all bust requests (with timeout)
        try {
            await Promise.race([
                Promise.all(bustPromises),
                new Promise((_, reject) => setTimeout(() => reject(new Error("Timeout")), 5000)),
            ]);
            console.log("Asset cache busting completed");
        }
        catch (error) {
            console.warn("Asset cache busting timed out or failed:", error);
        }
    };
    // Update service worker with more aggressive approach
    const updateServiceWorker = async () => {
        if (!enableServiceWorkerUpdate || !("serviceWorker" in navigator))
            return;
        try {
            const registrations = await navigator.serviceWorker.getRegistrations();
            for (const registration of registrations) {
                // Force update
                await registration.update();
                // If there's a waiting worker, skip waiting and activate
                if (registration.waiting) {
                    registration.waiting.postMessage({ type: "SKIP_WAITING" });
                }
                // Unregister and re-register for maximum freshness (aggressive)
                if (aggressiveCacheBusting) {
                    await registration.unregister();
                    console.log("Service Worker unregistered for fresh registration");
                }
            }
            console.log("Service Worker update completed");
        }
        catch (error) {
            console.error("Failed to update Service Worker:", error);
        }
    };
    // Enhanced reload with multiple strategies
    const refreshCacheAndReload = async () => {
        try {
            // Set reload flag
            safeLocalStorage.setItem("nf-app-reloading", "true");
            safeLocalStorage.setItem("nf-reload-timestamp", Date.now().toString());
            console.log("Starting aggressive cache clearing...");
            // Clear all caches
            await clearAllCaches();
            // Update service worker
            await updateServiceWorker();
            // Strategy 1: Try window.location.reload(true) for hard reload (some browsers)
            if (aggressiveCacheBusting) {
                try {
                    // @ts-ignore - forceReload parameter (deprecated but still works in some browsers)
                    window.location.reload(true);
                    return; // If this works, we're done
                }
                catch (e) {
                    console.log("Hard reload not supported, using alternative method");
                }
            }
            // Strategy 2: Change URL with cache busting and navigate
            const url = new URL(window.location.href);
            // Remove existing version parameters
            ["v", "version", "bust", "cache", "_t"].forEach((param) => {
                url.searchParams.delete(param);
            });
            // Add multiple cache-busting parameters
            const timestamp = Date.now();
            url.searchParams.set("v", timestamp.toString());
            url.searchParams.set("_cb", Math.random().toString(36).substr(2, 9));
            console.log("Reloading with cache-busted URL:", url.toString());
            // Small delay to ensure all cache operations complete
            setTimeout(() => {
                window.location.href = url.toString();
            }, 200);
        }
        catch (error) {
            console.error("Error during cache refresh and reload:", error);
            safeLocalStorage.removeItem("nf-app-reloading");
            // Fallback: simple reload
            setTimeout(() => {
                window.location.reload();
            }, 1000);
        }
    };
    // Enhanced version checking with more robust cache busting
    const checkAppVersion = useCallback(async () => {
        try {
            // Check if we're in a reload cycle
            const isReloading = safeLocalStorage.getItem("nf-app-reloading");
            const reloadTimestamp = safeLocalStorage.getItem("nf-reload-timestamp");
            if (isReloading) {
                // Check if reload is stuck (more than 30 seconds old)
                const now = Date.now();
                const reloadTime = parseInt(reloadTimestamp || "0");
                if (now - reloadTime > 30000) {
                    console.log("Reload appears stuck, clearing flags and continuing");
                    safeLocalStorage.removeItem("nf-app-reloading");
                    safeLocalStorage.removeItem("nf-reload-timestamp");
                }
                else {
                    console.log("Clearing reload flag and skipping version check");
                    safeLocalStorage.removeItem("nf-app-reloading");
                    safeLocalStorage.removeItem("nf-reload-timestamp");
                    retryCountRef.current = 0;
                    return;
                }
            }
            const oldAppVersion = safeLocalStorage.getItem("nf-build-app-version");
            console.log("Current stored version:", oldAppVersion);
            // More aggressive cache busting for version check
            const cacheBustParams = [
                `nonce=${Date.now()}`,
                `r=${Math.random()}`,
                `_=${new Date().getTime()}`,
            ].join("&");
            // Fetch with multiple cache-busting strategies
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000);
            const response = await fetch(`${publicUrl}/meta.json?${cacheBustParams}`, {
                signal: controller.signal,
                cache: "no-store",
                headers: {
                    "Cache-Control": "no-cache, no-store, must-revalidate",
                    Pragma: "no-cache",
                    Expires: "0",
                },
            });
            clearTimeout(timeoutId);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            const appVersion = await response.json();
            console.log("Latest version from server:", appVersion.version);
            if (!oldAppVersion || appVersion.version !== oldAppVersion) {
                console.log("Version mismatch detected, updating...");
                if (onVersionUpdate) {
                    onVersionUpdate(oldAppVersion, appVersion.version);
                }
                const success = safeLocalStorage.setItem("nf-build-app-version", appVersion.version);
                if (success) {
                    await refreshCacheAndReload();
                }
                else {
                    console.warn("Failed to update version in localStorage, forcing reload anyway");
                    window.location.reload();
                }
            }
            else {
                console.log("Version is up to date");
                retryCountRef.current = 0;
            }
        }
        catch (error) {
            console.error("Error checking app version:", error);
            if (retryCountRef.current < maxRetries) {
                retryCountRef.current++;
                console.log(`Retrying version check (${retryCountRef.current}/${maxRetries}) in ${retryDelay * retryCountRef.current}ms...`);
                setTimeout(() => {
                    checkAppVersion();
                }, retryDelay * retryCountRef.current);
            }
            else {
                console.error("Max retries reached for version check");
                retryCountRef.current = 0;
            }
        }
    }, [
        maxRetries,
        retryDelay,
        onVersionUpdate,
        enableServiceWorkerUpdate,
        aggressiveCacheBusting,
    ]);
    useEffect(() => {
        // Initial version check
        checkAppVersion();
        // Set up interval if specified
        if (checkInterval > 0) {
            intervalRef.current = setInterval(checkAppVersion, checkInterval);
        }
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [checkAppVersion, checkInterval]);
    return {
        checkVersion: checkAppVersion,
        forceRefresh: refreshCacheAndReload,
        clearAllCaches,
        bustAssetCache,
        clearReloadFlag: () => {
            safeLocalStorage.removeItem("nf-app-reloading");
            safeLocalStorage.removeItem("nf-reload-timestamp");
        },
    };
};
